import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Search, Users, UserCheck, Coffee, UserX, Wifi, AlarmClock } from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import {
  getAttendanceSocket,
  useAttendanceEvents,
} from "../../../services/attendanceSocket";
import useDebounced from "../../../utils/useDebounced";
import {
  breakLabel,
  STATE_META,
  formatClock,
  formatDuration,
  formatTime,
} from "../../../utils/attendance";

// Real-time view of the floor. The REST snapshot is the starting point; every
// clock-in, break and clock-out after that arrives over the /attendance socket,
// so the table moves without polling.
//
// Two safety nets, because a dashboard that silently goes stale is worse than
// one that is obviously broken:
//   - a reconnect re-fetches the snapshot (events sent while offline are lost)
//   - a slow background refresh catches anything a dropped event would miss

const REFRESH_MS = 60000;
const PAGE_SIZE = 20;

const STAT_TILES = [
  { key: "totalAgents", label: "Total Agents", icon: <Users size={20} />, color: "#0C237B", bg: "#eef2ff" },
  { key: "clockedIn", label: "Clocked In", icon: <UserCheck size={20} />, color: "#059669", bg: "#ecfdf5" },
  { key: "onBreak", label: "On Break", icon: <Coffee size={20} />, color: "#d97706", bg: "#fffbeb" },
  { key: "off", label: "Not Clocked In", icon: <UserX size={20} />, color: "#6b7280", bg: "#f3f4f6" },
  { key: "online", label: "Panel Open", icon: <Wifi size={20} />, color: "#0ea5e9", bg: "#f0f9ff" },
  { key: "lateToday", label: "Late Today", icon: <AlarmClock size={20} />, color: "#dc2626", bg: "#fef2f2" },
];

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("") || "?";

export default function LiveDashboard() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [filter, setFilter] = useState("all");
  const [live, setLive] = useState(false);
  const [busyId, setBusyId] = useState("");

  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const offsetRef = useRef(0);
  const [, setTick] = useState(0);

  // Search, state filter and paging are all server-side: with a page in hand
  // the browser cannot filter across agents it has never been sent.
  const load = useCallback(
    async (silent) => {
      if (!silent) setLoading(true);
      try {
        const { data } = await axiosInstance.get(apiEndPointsConfig.attendanceLive, {
          params: {
            page,
            limit: PAGE_SIZE,
            search: debouncedSearch || undefined,
            state: filter === "all" ? undefined : filter,
          },
        });
        if (data?.serverTime) offsetRef.current = new Date(data.serverTime).getTime() - Date.now();
        setRows(data?.data || []);
        // These counters span the whole floor, not this page -- the server
        // computes them before paging for exactly that reason.
        setStats(data?.stats || null);
        setPages(data?.pagination?.pages || 1);
        setTotal(data?.pagination?.total || 0);
        setError("");
      } catch (err) {
        setError(err?.response?.data?.message || "Could not load the live dashboard");
      } finally {
        setLoading(false);
      }
    },
    [page, debouncedSearch, filter]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filter]);

  // A transition for an agent on another page still moves the floor counters,
  // and under a state filter it can move a row in or out of the page entirely.
  // Patching what is on screen is not enough, so coalesce a quiet re-fetch
  // rather than firing one per event during a shift change.
  const resyncTimer = useRef(null);
  const scheduleResync = useCallback(() => {
    if (resyncTimer.current) clearTimeout(resyncTimer.current);
    resyncTimer.current = setTimeout(() => load(true), 2500);
  }, [load]);

  useEffect(() => () => resyncTimer.current && clearTimeout(resyncTimer.current), []);

  // Backstop poll. Cheap, and it means a missed event self-heals within a minute.
  useEffect(() => {
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // One-second repaint so the running timers advance.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Live feed ────────────────────────────────────────────────────────────
  const applyShift = useCallback((payload) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.agent.id !== payload.agentId) return r;
        const closed = payload.status === "clocked_out";
        return {
          ...r,
          state: closed ? "off" : payload.status,
          shift: closed ? null : payload,
          // The authoritative daily totals come from the next snapshot; this
          // keeps the row honest in the meantime.
          today: closed
            ? {
                ...r.today,
                shifts: (r.today?.shifts || 0) + 1,
                workedMs: (r.today?.workedMs || 0) + payload.totals.workedMs,
                breakMs: (r.today?.breakMs || 0) + payload.totals.breakMs,
                productiveMs: (r.today?.productiveMs || 0) + payload.totals.productiveMs,
                lastOutAt: payload.clockOutAt,
              }
            : r.today,
        };
      })
    );
  }, []);

  useAttendanceEvents(
    ["attendance.update", "agent.presence", "agent.created", "agent.updated", "agent.deleted"],
    (payload, event) => {
      setLive(true);
      if (event === "attendance.update") {
        applyShift(payload); // instant feedback for a row already on screen
        scheduleResync(); // then reconcile the floor counters and the page
      } else if (event === "agent.presence") {
        setRows((prev) =>
          prev.map((r) => (r.agent.id === payload.agentId ? { ...r, online: payload.online } : r))
        );
        scheduleResync();
      } else {
        // Roster changed -- the snapshot is the simplest correct answer.
        scheduleResync();
      }
    },
    () => load(true)
  );

  useEffect(() => {
    const s = getAttendanceSocket();
    if (!s) return undefined;
    const up = () => setLive(true);
    const down = () => setLive(false);
    s.on("connect", up);
    s.on("disconnect", down);
    if (s.connected) setLive(true);
    return () => {
      s.off("connect", up);
      s.off("disconnect", down);
    };
  }, []);

  const now = Date.now() + offsetRef.current;

  const elapsedFor = (row) => {
    if (!row.shift) return null;
    if (row.state === "on_break" && row.shift.currentBreak) {
      return now - new Date(row.shift.currentBreak.startAt).getTime();
    }
    let breakMs = 0;
    for (const b of row.shift.breaks || []) {
      const end = b.endAt ? new Date(b.endAt).getTime() : now;
      breakMs += Math.max(0, end - new Date(b.startAt).getTime());
    }
    return Math.max(0, now - new Date(row.shift.clockInAt).getTime() - breakMs);
  };

  const forceClockOut = async (row) => {
    if (!row.shift) return;
    setBusyId(row.agent.id);
    try {
      await axiosInstance.post(
        `${apiEndPointsConfig.attendanceRecords}/${row.shift.id}/force-clock-out`,
        { note: "Closed by admin from the live dashboard" }
      );
      await load(true);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not close that shift");
    } finally {
      setBusyId("");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* ── Headline counters ───────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STAT_TILES.map((t) => (
          <Grid item xs={6} sm={4} md={2} key={t.key}>
            <Card
              sx={{
                borderRadius: "14px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                cursor: ["clockedIn", "onBreak", "off"].includes(t.key) ? "pointer" : "default",
              }}
              onClick={() => {
                if (t.key === "clockedIn") setFilter(filter === "clocked_in" ? "all" : "clocked_in");
                if (t.key === "onBreak") setFilter(filter === "on_break" ? "all" : "on_break");
                if (t.key === "off") setFilter(filter === "off" ? "all" : "off");
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      bgcolor: t.bg,
                      color: t.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {t.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: t.color, lineHeight: 1.1 }}>
                      {stats?.[t.key] ?? 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6b7280" }} noWrap>
                      {t.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Controls ────────────────────────────────────────────────────── */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Search agent, user ID, team..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", sm: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#9ca3af" />
              </InputAdornment>
            ),
          }}
        />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(e, v) => v && setFilter(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="clocked_in">Clocked In</ToggleButton>
          <ToggleButton value="on_break">On Break</ToggleButton>
          <ToggleButton value="off">Off</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />

        <Tooltip title={live ? "Receiving live updates" : "Reconnecting - showing last known state"}>
          <Chip
            size="small"
            label={live ? "Live" : "Offline"}
            sx={{
              bgcolor: live ? "#ecfdf5" : "#fef2f2",
              color: live ? "#059669" : "#dc2626",
              fontWeight: 700,
            }}
          />
        </Tooltip>
        <Button size="small" onClick={() => load()} sx={{ color: "#0C237B" }}>
          Refresh
        </Button>
      </Paper>

      {/* ── The floor ───────────────────────────────────────────────────── */}
      <Paper sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "none", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, color: "#6b7280", bgcolor: "#f9fafb" } }}>
                <TableCell>Agent</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Running</TableCell>
                <TableCell>Clocked In</TableCell>
                <TableCell>Break</TableCell>
                <TableCell align="right">Productive Today</TableCell>
                <TableCell align="right">Break Today</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5, color: "#9ca3af" }}>
                    No agents match this view.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => {
                const meta = STATE_META[row.state] || STATE_META.off;
                const running = elapsedFor(row);
                return (
                  <TableRow key={row.agent.id} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ position: "relative" }}>
                          <Avatar sx={{ width: 34, height: 34, bgcolor: "#eef2ff", color: "#0C237B", fontSize: 13, fontWeight: 700 }}>
                            {initials(row.agent.name)}
                          </Avatar>
                          <Tooltip title={row.online ? "Agent panel open" : "Agent panel closed"}>
                            <Box
                              sx={{
                                position: "absolute",
                                right: -1,
                                bottom: -1,
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                border: "2px solid white",
                                bgcolor: row.online ? "#22c55e" : "#d1d5db",
                              }}
                            />
                          </Tooltip>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }} noWrap>
                            {row.agent.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#9ca3af" }} noWrap>
                            {row.agent.userid}
                            {row.agent.team ? ` · ${row.agent.team}` : ""}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={meta.label}
                        sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }}
                      />
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontWeight: 700,
                        color: row.state === "on_break" ? "#d97706" : row.state === "clocked_in" ? "#059669" : "#d1d5db",
                      }}
                    >
                      {running === null ? "--" : formatClock(running)}
                    </TableCell>

                    <TableCell>
                      {row.shift ? (
                        <>
                          {formatTime(row.shift.clockInAt)}
                          {row.shift.lateBy > 0 && (
                            <Tooltip title={`${formatDuration(row.shift.lateBy)} past rostered start`}>
                              <Chip
                                label="late"
                                size="small"
                                sx={{ ml: 0.5, height: 18, fontSize: 10, bgcolor: "#fef2f2", color: "#dc2626" }}
                              />
                            </Tooltip>
                          )}
                        </>
                      ) : (
                        <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                          {row.today?.lastOutAt ? `out ${formatTime(row.today.lastOutAt)}` : "--"}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {row.state === "on_break" && row.shift?.currentBreak ? (
                        <Typography variant="caption" sx={{ color: "#d97706", fontWeight: 600 }}>
                          {breakLabel(row.shift.currentBreak)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                          {row.shift ? `${row.shift.breaks.length} taken` : "--"}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right" sx={{ fontWeight: 600, color: "#059669" }}>
                      {formatDuration(row.today?.productiveMs || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "#6b7280" }}>
                      {formatDuration(row.today?.breakMs || 0)}
                    </TableCell>

                    <TableCell align="right">
                      {row.shift ? (
                        <Button
                          size="small"
                          disabled={busyId === row.agent.id}
                          onClick={() => forceClockOut(row)}
                          sx={{ color: "#dc2626", fontSize: 12, textTransform: "none" }}
                        >
                          Force clock out
                        </Button>
                      ) : (
                        <Typography variant="caption" sx={{ color: "#d1d5db" }}>
                          --
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {total > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              px: 2,
              py: 1.5,
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} of {total}
              {filter !== "all" ? " matching" : " agents"}
            </Typography>
            {pages > 1 && (
              <Pagination
                count={pages}
                page={page}
                onChange={(e, v) => setPage(v)}
                size="small"
                shape="rounded"
              />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
